import { useTranslation } from "react-i18next";
import {
  BookOpen,
  Check,
  Crown,
  Footprints,
  MessageCircle,
  Smartphone,
  Video,
  X,
} from "lucide-react";

import type { AnimalId } from "@/components/camp/campData";
import type { StagePerson } from "@/components/camp/FireStage";
import { NightStage } from "@/components/camp/NightStage";
import { Button } from "@/components/ui/button";

import { LandingDemoButton } from "./LandingDemo";
import { NightScope } from "./NightScope";
import { CornerAnimal, LANDING_CARD, LandingSection } from "./LandingSection";

/**
 * The second half of what the landing argues: what else a room can do, what it
 * never keeps, how it compares, the questions, and the call to light a fire.
 *
 * The words are the ones `docs/product/product.md` and `audiences.md` already
 * argue for. Nothing here claims a capability the room does not have, and
 * nothing names a price — what a paid tier contains is still open in
 * `docs/product/okrs.md`, and a landing page is not the place to decide it.
 */

/**
 * Each layer wears the icon the room gives that feature, in a colour of its
 * own, and its animal looks in from the corner (#945): the icon says what the
 * card is about at a glance, which an animal alone did not. Built when drawn,
 * not at import: the icons come from a chunk that imports this one back, and
 * read at import time they were `undefined` on the production landing.
 */
function layers(): readonly {
  key: string;
  icon: typeof Video;
  /** Sets `--layer` to a token of the palette, so the tile follows the colours. */
  colour: string;
  animal: AnimalId;
}[] {
  return [
    {
      key: "video",
      icon: Video,
      colour: "[--layer:var(--identity-sky)]",
      animal: "cat",
    },
    {
      key: "chat",
      icon: MessageCircle,
      colour: "[--layer:var(--identity-lilac)]",
      animal: "raccoon",
    },
    {
      key: "narrator",
      icon: BookOpen,
      colour: "[--layer:var(--identity-honey)]",
      animal: "owl",
    },
    {
      key: "move",
      icon: Footprints,
      colour: "[--layer:var(--identity-moss)]",
      animal: "deer",
    },
    {
      key: "host",
      icon: Crown,
      colour: "[--layer:var(--primary)]",
      animal: "bear",
    },
    {
      key: "phone",
      icon: Smartphone,
      colour: "[--layer:var(--identity-teal)]",
      animal: "mouse",
    },
  ];
}

export function LandingLayers() {
  const { t } = useTranslation();
  return (
    <LandingSection
      id="features"
      eyebrow={t("landing.layers.eyebrow")}
      title={t("landing.layers.title")}
    >
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {layers().map(({ key, icon: Icon, colour, animal }) => (
          <li
            key={key}
            data-testid={`landing-layer-${key}`}
            className={`${colour} ${LANDING_CARD} relative flex flex-col gap-4 overflow-hidden`}
          >
            <CornerAnimal animal={animal} />
            <span
              aria-hidden="true"
              className="grid size-12 place-items-center rounded-2xl bg-[hsl(var(--layer)/0.18)] text-[hsl(var(--layer))] shadow-glow-sm"
            >
              <Icon className="size-6" />
            </span>
            <div className="pr-10">
              <h3 className="mb-1 text-lg font-semibold">
                {t(`landing.layers.${key}.title`)}
              </h3>
              <p className="text-pretty text-sm text-muted-foreground">
                {t(`landing.layers.${key}.body`)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </LandingSection>
  );
}

const PROMISES = ["records", "direct", "accounts"] as const;
const ROWS = ["install", "account", "place", "borrowed", "invite"] as const;
/** Where the comparison is a plain yes, it is marked; the invitation is said. */
const MARKED: ReadonlySet<string> = new Set([
  "install",
  "account",
  "place",
  "borrowed",
]);

export function LandingTrust() {
  const { t } = useTranslation();
  return (
    <LandingSection
      id="trust"
      eyebrow={t("landing.trust.eyebrow")}
      title={t("landing.trust.title")}
    >
      <ul className="grid gap-4 md:grid-cols-3">
        {PROMISES.map((promise) => (
          <li key={promise} className={LANDING_CARD}>
            <p className="text-xl font-bold tracking-tight whitespace-nowrap text-primary">
              {t(`landing.trust.${promise}.figure`)}
            </p>
            <p className="mt-2 text-pretty text-muted-foreground">
              {t(`landing.trust.${promise}.body`)}
            </p>
          </li>
        ))}
      </ul>
      <div className="mt-10 overflow-x-auto rounded-3xl border border-border">
        <table className="w-full border-collapse text-left text-sm sm:text-base">
          <caption className="sr-only">
            {t("landing.trust.compare.caption")}
          </caption>
          <thead className="bg-card text-sm text-muted-foreground">
            <tr>
              <td className="p-4" />
              <th scope="col" className="p-4 text-primary">
                {t("app.name")}
              </th>
              <th scope="col" className="p-4">
                {t("landing.trust.compare.them")}
              </th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row} className="border-t border-border">
                <th scope="row" className="p-4 font-medium">
                  {t(`landing.trust.compare.${row}.label`)}
                </th>
                <td className="bg-primary/6 p-4 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    {MARKED.has(row) ? (
                      <Check
                        className="size-4 text-primary"
                        aria-hidden="true"
                      />
                    ) : null}
                    {t(`landing.trust.compare.${row}.us`)}
                  </span>
                </td>
                <td className="p-4 text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    {row === "place" ? (
                      <X className="size-4 text-stop" aria-hidden="true" />
                    ) : null}
                    {t(`landing.trust.compare.${row}.them`)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </LandingSection>
  );
}

const QUESTIONS = ["account", "size", "recorded", "phone", "better"] as const;

export function LandingFaq() {
  const { t } = useTranslation();
  return (
    <LandingSection
      id="faq"
      eyebrow={t("landing.faq.eyebrow")}
      title={t("landing.faq.title")}
    >
      <div className="grid max-w-3xl gap-2.5">
        {QUESTIONS.map((key) => (
          <details
            key={key}
            className="group rounded-2xl border border-border bg-card px-5 open:pb-5"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-lg font-semibold [&::-webkit-details-marker]:hidden">
              {t(`landing.faq.${key}Q`)}
              <span
                aria-hidden="true"
                className="text-lg font-normal text-primary transition group-open:rotate-45"
              >
                +
              </span>
            </summary>
            <p className="text-pretty text-muted-foreground">
              {t(`landing.faq.${key}A`)}
            </p>
          </details>
        ))}
      </div>
    </LandingSection>
  );
}

/**
 * The ones already at the fire at the end of the page, with a place kept. The
 * seats are a side, the back and the other side, so no two stand one behind the
 * other; the seat nearest the viewer is the one kept.
 */
const WAITING: readonly Omit<StagePerson, "name">[] = [
  { id: "kate", beast: "fox", seat: 3 },
  { id: "dima", beast: "bear", seat: 5 },
  { id: "anna", beast: "deer", seat: 6 },
  { id: "leo", beast: "raccoon", seat: 7, raised: true },
  { id: "sasha", beast: "owl", seat: 9 },
];

export function LandingClosing({ onDemo }: { onDemo: () => void }) {
  const { t } = useTranslation();
  const people: StagePerson[] = WAITING.map((person) => ({
    ...person,
    name: t(`landing.people.${person.id}`),
    message: person.id === "anna" ? t("landing.closing.waiting") : undefined,
  }));
  return (
    <section
      data-testid="landing-closing"
      className="mx-auto w-full max-w-6xl px-4 py-20 text-center sm:py-28"
    >
      <div className="mx-auto max-w-3xl">
        <h2 className="text-balance text-xl font-bold tracking-tight">
          {t("landing.closing.title")}
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-pretty text-lg text-muted-foreground">
          {t("landing.closing.body")}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg">
            <a href="/home" data-testid="landing-closing-create">
              {t("landing.hero.create")}
            </a>
          </Button>
          <LandingDemoButton
            onStart={onDemo}
            label={t("landing.closing.demo")}
            testId="landing-closing-demo"
          />
        </div>
      </div>
      {/* The seat nearest the viewer is left empty: it is the visitor's. */}
      <NightScope className="mt-12">
        <div aria-hidden="true">
          <NightStage people={people} className="h-64 min-h-0 sm:h-80" />
        </div>
      </NightScope>
    </section>
  );
}
